import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  Autocomplete,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers-pro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faPlus,
  faTrash,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import { useDebounce } from "use-debounce";
import Button from "components/Button";
import { ModalFieldScope } from "components/ModalFieldScope";
import contractsService from "api/contractsService";
import {
  CRU_CHANGE_TYPE_LABELS,
  CRU_CONFIDENTIALITY_LABELS,
  CRU_PERIOD_UNIT_LABELS,
  CRU_SIDE_TYPE_LABELS,
  CRU_STATUS_LABELS,
  CruAgreementStatus,
  CruChangeType,
  CruConfidentialityBasis,
  CruPeriodUnit,
  CruSideType,
  IContractChange,
  IContractConfidentiality,
  IContractCruMetric,
  IContractCruSide,
  emptyCruSide,
} from "dto/Contracts/IContractCru";
import { periodByDates, validateCruMetric } from "utils/validateCruMetric";
import styles from "./styles.module.scss";

// ── Metryka umowy wg CRU — port kreatora ContractCruMetricView (Magistrat) ──
//
// Cztery kroki jako zwijane sekcje: dane podstawowe, strony, przedmiot i wartość,
// status i zmiany. Ten sam formularz obsługuje trzy źródła umowy: pismo z edytora
// (tworzenie pisma — walidacja odroczona), załącznik przesyłki (kreator rejestracji —
// walidacja odroczona) i wpis w rejestrze (pełna walidacja przed zapisem/publikacją).

interface Props {
  title?: string;
  subtitle?: string;
  initial: IContractCruMetric;
  /**
   * Walidacja odroczona (tworzenie pisma / rejestracja przesyłki): braki są
   * pokazywane jako ostrzeżenia, ale zapis jest możliwy — komplet wymagany dopiero
   * w rejestrze przed publikacją (jak DeferValidation w Magistracie).
   */
  deferValidation?: boolean;
  readOnly?: boolean;
  saving?: boolean;
  saveLabel?: string;
  onSave: (metric: IContractCruMetric) => void;
  onClose: () => void;
}

const isoDate = (v: moment.Moment | null) =>
  v && v.isValid() ? v.format("YYYY-MM-DD") : null;
const toMoment = (v?: string | null) => (v ? moment(v) : null);

const SIDE_TYPES = Object.keys(CRU_SIDE_TYPE_LABELS) as CruSideType[];
const PERIOD_UNITS = Object.keys(CRU_PERIOD_UNIT_LABELS) as CruPeriodUnit[];
const BASES = Object.keys(
  CRU_CONFIDENTIALITY_LABELS
) as CruConfidentialityBasis[];
const CHANGE_TYPES = Object.keys(CRU_CHANGE_TYPE_LABELS) as CruChangeType[];

const sideLabel = (s: IContractCruSide) => {
  if (s.Confidentiality) return "(dane strony niejawne)";
  if (s.SideType === "SU02")
    return (
      `${s.FirstName ?? ""} ${s.LastName ?? ""}`.trim() || "osoba fizyczna"
    );
  return s.Name || s.Nip || s.Regon || "";
};

// ── Sekcja / krok ────────────────────────────────────────────────────────────

const Step = ({
  no,
  title,
  errors,
  warn,
  open,
  onToggle,
  summary,
  children,
}: {
  no: number;
  title: string;
  errors: string[];
  warn: boolean;
  open: boolean;
  onToggle: () => void;
  summary?: string;
  children: React.ReactNode;
}) => (
  <div
    className={`${styles.section}${
      errors.length && !warn ? ` ${styles.sectionError}` : ""
    }`}
  >
    <button type="button" className={styles.sectionToggle} onClick={onToggle}>
      <span className={styles.sectionTitle}>
        <span
          className={`${styles.stepNo}${
            errors.length && !warn ? ` ${styles.stepNoError}` : ""
          }`}
        >
          {no}
        </span>
        {title}
      </span>
      <span className={styles.sectionRight}>
        {!open && summary}
        <FontAwesomeIcon
          icon={open ? faChevronUp : faChevronDown}
          className={styles.sectionChevron}
        />
      </span>
    </button>
    {open && (
      <div className={styles.sectionBody}>
        {children}
        {errors.length > 0 && (
          <ul
            className={`${styles.errors}${warn ? ` ${styles.warnings}` : ""}`}
          >
            {errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        )}
      </div>
    )}
  </div>
);

// ── Wyłączenie jawności (strona / przedmiot / wartość) ───────────────────────

const ConfidentialityFields = ({
  value,
  onChange,
  disabled,
}: {
  value: IContractConfidentiality;
  onChange: (v: IContractConfidentiality) => void;
  disabled?: boolean;
}) => (
  <>
    <div className={styles.row2}>
      <FormControl size="small" fullWidth>
        <InputLabel>Podstawa wyłączenia jawności</InputLabel>
        <Select
          label="Podstawa wyłączenia jawności"
          value={value.Basis}
          disabled={disabled}
          onChange={(e) =>
            onChange({
              ...value,
              Basis: e.target.value as CruConfidentialityBasis,
            })
          }
        >
          {BASES.map((b) => (
            <MenuItem key={b} value={b}>
              {CRU_CONFIDENTIALITY_LABELS[b]}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        size="small"
        fullWidth
        label="Organ lub osoba wyłączająca jawność"
        value={value.Authority ?? ""}
        disabled={disabled}
        onChange={(e) => onChange({ ...value, Authority: e.target.value })}
      />
    </div>
    {value.Basis === "INNA" && (
      <TextField
        size="small"
        fullWidth
        label="Komentarz (wymagany dla podstawy „Inna”)"
        value={value.Comment ?? ""}
        disabled={disabled}
        onChange={(e) => onChange({ ...value, Comment: e.target.value })}
      />
    )}
  </>
);

// ── Strona umowy ─────────────────────────────────────────────────────────────

const SideCard = ({
  index,
  side,
  onChange,
  onRemove,
  disabled,
}: {
  index: number;
  side: IContractCruSide;
  onChange: (s: IContractCruSide) => void;
  onRemove: () => void;
  disabled?: boolean;
}) => {
  const set = (patch: Partial<IContractCruSide>) =>
    onChange({ ...side, ...patch });
  const isPerson = side.SideType === "SU02";
  const isPoland = (side.Country ?? "").toUpperCase() === "PL";
  const confidential = !!side.Confidentiality;

  return (
    <div className={styles.sideCard}>
      <div className={styles.sideHeader}>
        <span className={styles.sideTitle}>
          Strona {index + 1}
          {sideLabel(side) ? ` — ${sideLabel(side)}` : ""}
        </span>
        <button
          type="button"
          className={styles.iconBtn}
          onClick={onRemove}
          disabled={disabled}
          title="Usuń stronę"
        >
          <FontAwesomeIcon icon={faTrash} />
        </button>
      </div>

      <div className={styles.row3}>
        <FormControl size="small" fullWidth>
          <InputLabel>Rodzaj strony</InputLabel>
          <Select
            label="Rodzaj strony"
            value={side.SideType}
            disabled={disabled}
            onChange={(e) => set({ SideType: e.target.value as CruSideType })}
          >
            {SIDE_TYPES.map((t) => (
              <MenuItem key={t} value={t}>
                {CRU_SIDE_TYPE_LABELS[t]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          fullWidth
          label="Kraj (kod ISO)"
          value={side.Country ?? ""}
          disabled={disabled}
          inputProps={{ maxLength: 2, style: { textTransform: "uppercase" } }}
          onChange={(e) => set({ Country: e.target.value.toUpperCase() })}
        />
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={side.IsConsortium}
              disabled={disabled}
              onChange={(e) => set({ IsConsortium: e.target.checked })}
            />
          }
          label="Konsorcjum"
        />
      </div>

      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={confidential}
            disabled={disabled}
            onChange={(e) =>
              set({
                Confidentiality: e.target.checked
                  ? { Basis: "ART_5_UST_2", Authority: "", Comment: "" }
                  : null,
              })
            }
          />
        }
        label="Wyłączenie jawności danych strony"
      />

      {confidential ? (
        <ConfidentialityFields
          value={side.Confidentiality!}
          disabled={disabled}
          onChange={(v) => set({ Confidentiality: v })}
        />
      ) : isPerson ? (
        <div className={styles.row2}>
          <TextField
            size="small"
            fullWidth
            label="Imię"
            value={side.FirstName ?? ""}
            disabled={disabled}
            onChange={(e) => set({ FirstName: e.target.value })}
          />
          <TextField
            size="small"
            fullWidth
            label="Nazwisko"
            value={side.LastName ?? ""}
            disabled={disabled}
            onChange={(e) => set({ LastName: e.target.value })}
          />
        </div>
      ) : (
        <>
          <div className={styles.row3}>
            <TextField
              size="small"
              fullWidth
              label="Nazwa"
              value={side.Name ?? ""}
              disabled={disabled}
              onChange={(e) => set({ Name: e.target.value })}
            />
            <TextField
              size="small"
              fullWidth
              label="NIP"
              value={side.Nip ?? ""}
              disabled={disabled || !isPoland}
              onChange={(e) => set({ Nip: e.target.value })}
            />
            <TextField
              size="small"
              fullWidth
              label="REGON"
              value={side.Regon ?? ""}
              disabled={disabled || !isPoland}
              onChange={(e) => set({ Regon: e.target.value })}
            />
          </div>
          {(side.SideType === "SU01" || !isPoland) && (
            <>
              <div className={styles.row3}>
                <TextField
                  size="small"
                  fullWidth
                  label="Ulica"
                  value={side.Street ?? ""}
                  disabled={disabled}
                  onChange={(e) => set({ Street: e.target.value })}
                />
                <TextField
                  size="small"
                  fullWidth
                  label="Nr nieruchomości"
                  value={side.BuildingNumber ?? ""}
                  disabled={disabled}
                  onChange={(e) => set({ BuildingNumber: e.target.value })}
                />
                <TextField
                  size="small"
                  fullWidth
                  label="Nr lokalu"
                  value={side.ApartmentNumber ?? ""}
                  disabled={disabled}
                  onChange={(e) => set({ ApartmentNumber: e.target.value })}
                />
              </div>
              <div className={styles.row2}>
                <TextField
                  size="small"
                  fullWidth
                  label="Kod pocztowy"
                  value={side.PostalCode ?? ""}
                  disabled={disabled}
                  onChange={(e) => set({ PostalCode: e.target.value })}
                />
                <TextField
                  size="small"
                  fullWidth
                  label="Miejscowość"
                  value={side.City ?? ""}
                  disabled={disabled}
                  onChange={(e) => set({ City: e.target.value })}
                />
              </div>
              {isPoland && (
                <div className={styles.row3}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Województwo"
                    value={side.Voivodeship ?? ""}
                    disabled={disabled}
                    onChange={(e) => set({ Voivodeship: e.target.value })}
                  />
                  <TextField
                    size="small"
                    fullWidth
                    label="Powiat"
                    value={side.County ?? ""}
                    disabled={disabled}
                    onChange={(e) => set({ County: e.target.value })}
                  />
                  <TextField
                    size="small"
                    fullWidth
                    label="Gmina / miasto / dzielnica"
                    value={side.Municipality ?? ""}
                    disabled={disabled}
                    onChange={(e) => set({ Municipality: e.target.value })}
                  />
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

// ── Modal ────────────────────────────────────────────────────────────────────

const ContractCruMetricModal = ({
  title = "Metryka umowy (CRU)",
  subtitle,
  initial,
  deferValidation = false,
  readOnly = false,
  saving = false,
  saveLabel = "Zapisz",
  onSave,
  onClose,
}: Props) => {
  const [m, setM] = useState<IContractCruMetric>(() => ({
    ...initial,
    Sides: initial.Sides ?? [],
    Changes: initial.Changes ?? [],
  }));
  const [open, setOpen] = useState<Record<number, boolean>>({
    1: true,
    2: true,
    3: true,
    4: true,
  });
  const [touched, setTouched] = useState(false);
  const [periodMode, setPeriodMode] = useState<"count" | "dates">(() =>
    periodByDates(initial) ? "dates" : "count"
  );

  const set = (patch: Partial<IContractCruMetric>) =>
    setM((prev) => ({ ...prev, ...patch }));

  const errors = useMemo(() => validateCruMetric(m), [m]);
  const hasErrors = errors.some((e) => e.length > 0);
  // Ostrzeżenia (walidacja odroczona) pokazujemy od razu; błędy blokujące — po
  // pierwszej próbie zapisu, żeby nie straszyć czerwienią pustego formularza.
  const showErrors = deferValidation || touched;

  // ── Podpowiedzi stron z dotychczasowych umów CRU firmy ──
  const [suggestQuery, setSuggestQuery] = useState("");
  const [debouncedQuery] = useDebounce(suggestQuery, 300);
  const [suggestions, setSuggestions] = useState<IContractCruSide[]>([]);
  useEffect(() => {
    if (readOnly) return;
    let cancelled = false;
    contractsService
      .suggestSides(debouncedQuery)
      .then((list) => !cancelled && setSuggestions(list))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, readOnly]);

  const updateSide = (i: number, s: IContractCruSide) =>
    set({ Sides: m.Sides.map((x, j) => (j === i ? s : x)) });
  const addSide = (s: IContractCruSide = emptyCruSide()) =>
    set({ Sides: [...m.Sides, { ...s, Id: null, SideOrder: m.Sides.length }] });
  const removeSide = (i: number) =>
    set({ Sides: m.Sides.filter((_, j) => j !== i) });

  const updateChange = (i: number, c: IContractChange) =>
    set({ Changes: m.Changes.map((x, j) => (j === i ? c : x)) });
  const addChange = () =>
    set({
      Changes: [
        ...m.Changes,
        {
          ChangeType: "TSU01",
          ChangeDate: moment().format("YYYY-MM-DD"),
          Comment: "",
        },
      ],
    });
  const removeChange = (i: number) =>
    set({ Changes: m.Changes.filter((_, j) => j !== i) });

  const handleSave = () => {
    setTouched(true);
    if (hasErrors && !deferValidation) {
      setOpen({ 1: true, 2: true, 3: true, 4: true });
      return;
    }
    const cleaned: IContractCruMetric = {
      ...m,
      IsCruContract: true,
      AgreementNumber: m.HasNoAgreementNumber ? null : m.AgreementNumber,
      PeriodCount:
        m.IndefinitePeriod || periodMode === "dates" ? null : m.PeriodCount,
      PeriodUnit:
        m.IndefinitePeriod || periodMode === "dates" ? null : m.PeriodUnit,
      PeriodFrom:
        m.IndefinitePeriod || periodMode === "count" ? null : m.PeriodFrom,
      PeriodTo:
        m.IndefinitePeriod || periodMode === "count" ? null : m.PeriodTo,
      Sides: m.Sides.map((s, i) => ({ ...s, SideOrder: i })),
    };
    onSave(cleaned);
  };

  const toggle = (n: number) => setOpen((p) => ({ ...p, [n]: !p[n] }));

  const isPublished = !!m.CruAgreementId && !m.CruWithdrawn;

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <ModalFieldScope>
        <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
          <div className={styles.header}>
            <div>
              <div className={styles.title}>{title}</div>
              {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
            </div>
            <button type="button" className={styles.closeBtn} onClick={onClose}>
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>

          <div className={styles.body}>
            {m.CruAgreementId && (
              <div
                className={`${styles.status} ${
                  m.CruWithdrawn
                    ? styles.statusWithdrawn
                    : styles.statusPublished
                }`}
              >
                <span>
                  {m.CruWithdrawn
                    ? "Wycofana z publikacji w CRU"
                    : "Opublikowana w CRU"}
                </span>
                {m.CruSystemNumber && (
                  <span>
                    nr systemowy: <b>{m.CruSystemNumber}</b>
                  </span>
                )}
                {m.CruProcessStatus && (
                  <span>
                    status: <b>{m.CruProcessStatus}</b>
                  </span>
                )}
                {m.CruPublicationDate && (
                  <span>
                    publikacja:{" "}
                    <b>{moment(m.CruPublicationDate).format("DD.MM.YYYY")}</b>
                  </span>
                )}
                {m.CruLastUpdateDate && (
                  <span>
                    ostatnia zmiana:{" "}
                    <b>
                      {moment(m.CruLastUpdateDate).format("DD.MM.YYYY HH:mm")}
                    </b>
                  </span>
                )}
              </div>
            )}

            {deferValidation && (
              <div className={styles.hint}>
                Metrykę można uzupełnić częściowo — komplet danych będzie
                wymagany w rejestrze umów przed publikacją w CRU.
              </div>
            )}

            {/* ── Krok 1: dane podstawowe ── */}
            <Step
              no={1}
              title="Dane podstawowe"
              errors={showErrors ? errors[0] : []}
              warn={deferValidation}
              open={open[1]}
              onToggle={() => toggle(1)}
              summary={
                m.AgreementNumber ??
                (m.HasNoAgreementNumber ? "bez numeru" : "")
              }
            >
              <div className={styles.row2}>
                <TextField
                  size="small"
                  fullWidth
                  label="Numer umowy"
                  value={m.AgreementNumber ?? ""}
                  disabled={readOnly || m.HasNoAgreementNumber}
                  onChange={(e) => set({ AgreementNumber: e.target.value })}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={m.HasNoAgreementNumber}
                      disabled={readOnly}
                      onChange={(e) =>
                        set({ HasNoAgreementNumber: e.target.checked })
                      }
                    />
                  }
                  label="Brak numeru umowy"
                />
              </div>
              <div className={styles.row2}>
                <DatePicker
                  label="Data zawarcia"
                  value={toMoment(m.AgreementDate)}
                  disabled={readOnly}
                  onChange={(v) => set({ AgreementDate: isoDate(v) })}
                  slotProps={{ textField: { size: "small", fullWidth: true } }}
                />
                <TextField
                  size="small"
                  fullWidth
                  label="Komórka wewnętrzna JSFP"
                  value={m.InternalUnit ?? ""}
                  disabled={readOnly}
                  onChange={(e) => set({ InternalUnit: e.target.value })}
                />
              </div>

              <span className={styles.label}>Okres obowiązywania</span>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={m.IndefinitePeriod}
                    disabled={readOnly}
                    onChange={(e) =>
                      set({ IndefinitePeriod: e.target.checked })
                    }
                  />
                }
                label="Umowa na czas nieoznaczony"
              />
              {!m.IndefinitePeriod && (
                <>
                  <RadioGroup
                    row
                    value={periodMode}
                    onChange={(e) =>
                      setPeriodMode(e.target.value as "count" | "dates")
                    }
                  >
                    <FormControlLabel
                      value="count"
                      control={<Radio size="small" />}
                      label="Liczba i jednostka"
                      disabled={readOnly}
                    />
                    <FormControlLabel
                      value="dates"
                      control={<Radio size="small" />}
                      label="Od – do"
                      disabled={readOnly}
                    />
                  </RadioGroup>
                  {periodMode === "count" ? (
                    <div className={styles.row2}>
                      <TextField
                        size="small"
                        fullWidth
                        type="number"
                        label="Liczba"
                        value={m.PeriodCount ?? ""}
                        disabled={readOnly}
                        onChange={(e) =>
                          set({
                            PeriodCount:
                              e.target.value === ""
                                ? null
                                : Number(e.target.value),
                          })
                        }
                      />
                      <FormControl size="small" fullWidth>
                        <InputLabel>Jednostka</InputLabel>
                        <Select
                          label="Jednostka"
                          value={m.PeriodUnit ?? ""}
                          disabled={readOnly}
                          onChange={(e) =>
                            set({
                              PeriodUnit: (e.target.value ||
                                null) as CruPeriodUnit | null,
                            })
                          }
                        >
                          {PERIOD_UNITS.map((u) => (
                            <MenuItem key={u} value={u}>
                              {CRU_PERIOD_UNIT_LABELS[u]}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                  ) : (
                    <div className={styles.row2}>
                      <DatePicker
                        label="Okres od"
                        value={toMoment(m.PeriodFrom)}
                        disabled={readOnly}
                        onChange={(v) => set({ PeriodFrom: isoDate(v) })}
                        slotProps={{
                          textField: { size: "small", fullWidth: true },
                        }}
                      />
                      <DatePicker
                        label="Okres do"
                        value={toMoment(m.PeriodTo)}
                        disabled={readOnly}
                        onChange={(v) => set({ PeriodTo: isoDate(v) })}
                        slotProps={{
                          textField: { size: "small", fullWidth: true },
                        }}
                      />
                    </div>
                  )}
                </>
              )}
            </Step>

            {/* ── Krok 2: strony ── */}
            <Step
              no={2}
              title="Strony umowy"
              errors={showErrors ? errors[1] : []}
              warn={deferValidation}
              open={open[2]}
              onToggle={() => toggle(2)}
              summary={m.Sides.map(sideLabel).filter(Boolean).join(", ")}
            >
              <span className={styles.hint}>
                Własna jednostka (JSFP) jest dodawana automatycznie jako
                pierwsza strona umowy na podstawie REGON-u z ustawień integracji
                CRU — nie wpisuj jej tutaj. Dla strony z Polski do CRU trafia
                tylko NIP albo REGON (nazwę CRU pobiera sam).
              </span>
              {!readOnly && (
                <Autocomplete
                  size="small"
                  options={suggestions}
                  getOptionLabel={(o) => sideLabel(o)}
                  filterOptions={(o) => o}
                  inputValue={suggestQuery}
                  onInputChange={(_, v) => setSuggestQuery(v)}
                  value={null}
                  onChange={(_, v) => {
                    if (v) {
                      addSide(v);
                      setSuggestQuery("");
                    }
                  }}
                  renderOption={(props, o) => (
                    <li
                      {...props}
                      key={`${o.Nip}-${o.Regon}-${o.Name}-${o.LastName}`}
                    >
                      <div className={styles.suggestOption}>
                        <span>{sideLabel(o)}</span>
                        <span className={styles.suggestMeta}>
                          {CRU_SIDE_TYPE_LABELS[o.SideType]}
                          {o.Nip ? ` · NIP ${o.Nip}` : ""}
                          {o.Regon ? ` · REGON ${o.Regon}` : ""}
                          {o.City ? ` · ${o.City}` : ""}
                        </span>
                      </div>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Dodaj stronę z poprzednich umów (nazwa / NIP / REGON / nazwisko)"
                    />
                  )}
                />
              )}
              {m.Sides.map((s, i) => (
                <SideCard
                  key={i}
                  index={i}
                  side={s}
                  disabled={readOnly}
                  onChange={(v) => updateSide(i, v)}
                  onRemove={() => removeSide(i)}
                />
              ))}
              {!readOnly && (
                <button
                  type="button"
                  className={styles.addBtn}
                  onClick={() => addSide()}
                >
                  <FontAwesomeIcon icon={faPlus} /> Dodaj stronę
                </button>
              )}
            </Step>

            {/* ── Krok 3: przedmiot i wartość ── */}
            <Step
              no={3}
              title="Przedmiot i wartość"
              errors={showErrors ? errors[2] : []}
              warn={deferValidation}
              open={open[3]}
              onToggle={() => toggle(3)}
              summary={
                m.SubjectConfidentiality
                  ? "przedmiot niejawny"
                  : (m.Subject ?? "").slice(0, 60)
              }
            >
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={!!m.SubjectConfidentiality}
                    disabled={readOnly}
                    onChange={(e) =>
                      set({
                        SubjectConfidentiality: e.target.checked
                          ? { Basis: "ART_5_UST_2", Authority: "", Comment: "" }
                          : null,
                      })
                    }
                  />
                }
                label="Wyłączenie jawności przedmiotu umowy"
              />
              {m.SubjectConfidentiality ? (
                <ConfidentialityFields
                  value={m.SubjectConfidentiality}
                  disabled={readOnly}
                  onChange={(v) => set({ SubjectConfidentiality: v })}
                />
              ) : (
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  label="Przedmiot umowy"
                  value={m.Subject ?? ""}
                  disabled={readOnly}
                  onChange={(e) => set({ Subject: e.target.value })}
                />
              )}

              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={!!m.ValueConfidentiality}
                    disabled={readOnly}
                    onChange={(e) =>
                      set({
                        ValueConfidentiality: e.target.checked
                          ? { Basis: "ART_5_UST_2", Authority: "", Comment: "" }
                          : null,
                      })
                    }
                  />
                }
                label="Wyłączenie jawności wartości umowy"
              />
              {m.ValueConfidentiality ? (
                <ConfidentialityFields
                  value={m.ValueConfidentiality}
                  disabled={readOnly}
                  onChange={(v) => set({ ValueConfidentiality: v })}
                />
              ) : (
                <div className={styles.row2}>
                  <TextField
                    size="small"
                    fullWidth
                    type="number"
                    label="Wartość przedmiotu (PLN)"
                    value={m.Value ?? ""}
                    disabled={readOnly}
                    inputProps={{ min: 0, step: 0.01 }}
                    onChange={(e) =>
                      set({
                        Value:
                          e.target.value === "" ? null : Number(e.target.value),
                      })
                    }
                  />
                  <TextField
                    size="small"
                    fullWidth
                    label="Opis wartości (opcjonalnie)"
                    value={m.ValueDescription ?? ""}
                    disabled={readOnly}
                    onChange={(e) => set({ ValueDescription: e.target.value })}
                  />
                </div>
              )}

              <span className={styles.label}>
                Umowa finansowana ze środków, o których mowa w art. 5 ust. 1 pkt
                2-3 ustawy o finansach publicznych
              </span>
              <RadioGroup
                row
                value={
                  m.FinancedFromOtherFunds == null
                    ? ""
                    : m.FinancedFromOtherFunds
                    ? "1"
                    : "0"
                }
                onChange={(e) =>
                  set({ FinancedFromOtherFunds: e.target.value === "1" })
                }
              >
                <FormControlLabel
                  value="1"
                  control={<Radio size="small" />}
                  label="Tak"
                  disabled={readOnly}
                />
                <FormControlLabel
                  value="0"
                  control={<Radio size="small" />}
                  label="Nie"
                  disabled={readOnly}
                />
              </RadioGroup>
            </Step>

            {/* ── Krok 4: status i zmiany ── */}
            <Step
              no={4}
              title="Status i zmiany umowy"
              errors={showErrors ? errors[3] : []}
              warn={deferValidation}
              open={open[4]}
              onToggle={() => toggle(4)}
              summary={
                m.AgreementStatus
                  ? `${CRU_STATUS_LABELS[m.AgreementStatus]}${
                      m.Changes.length ? `, zmian: ${m.Changes.length}` : ""
                    }`
                  : ""
              }
            >
              <div className={styles.row2}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Status umowy</InputLabel>
                  <Select
                    label="Status umowy"
                    value={m.AgreementStatus ?? ""}
                    disabled={readOnly}
                    onChange={(e) =>
                      set({
                        AgreementStatus: (e.target.value ||
                          null) as CruAgreementStatus | null,
                      })
                    }
                  >
                    <MenuItem value="Aktywna">
                      {CRU_STATUS_LABELS.Aktywna}
                    </MenuItem>
                    <MenuItem value="Nieaktywna">
                      {CRU_STATUS_LABELS.Nieaktywna}
                    </MenuItem>
                  </Select>
                </FormControl>
                <DatePicker
                  label="Data zakończenia umowy"
                  value={toMoment(m.AgreementEndDate)}
                  disabled={readOnly}
                  onChange={(v) => set({ AgreementEndDate: isoDate(v) })}
                  slotProps={{ textField: { size: "small", fullWidth: true } }}
                />
              </div>

              <span className={styles.label}>Zmiany umowy</span>
              {isPublished && (
                <span className={styles.hint}>
                  Aktualizacja w CRU wymaga co najmniej jednej zmiany — bez
                  wpisu wysyłana jest „Korekta danych".
                </span>
              )}
              {m.Changes.map((c, i) => (
                <div key={i} className={styles.changeRow}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Rodzaj zmiany</InputLabel>
                    <Select
                      label="Rodzaj zmiany"
                      value={c.ChangeType}
                      disabled={readOnly}
                      onChange={(e) =>
                        updateChange(i, {
                          ...c,
                          ChangeType: e.target.value as CruChangeType,
                        })
                      }
                    >
                      {CHANGE_TYPES.map((t) => (
                        <MenuItem key={t} value={t}>
                          {CRU_CHANGE_TYPE_LABELS[t]}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <DatePicker
                    label="Data"
                    value={toMoment(c.ChangeDate)}
                    disabled={readOnly}
                    onChange={(v) =>
                      updateChange(i, { ...c, ChangeDate: isoDate(v) ?? "" })
                    }
                    slotProps={{
                      textField: { size: "small", fullWidth: true },
                    }}
                  />
                  <TextField
                    size="small"
                    fullWidth
                    label="Komentarz"
                    value={c.Comment ?? ""}
                    disabled={readOnly}
                    onChange={(e) =>
                      updateChange(i, { ...c, Comment: e.target.value })
                    }
                  />
                  <button
                    type="button"
                    className={styles.iconBtn}
                    disabled={readOnly}
                    onClick={() => removeChange(i)}
                    title="Usuń zmianę"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              ))}
              {!readOnly && (
                <button
                  type="button"
                  className={styles.addBtn}
                  onClick={addChange}
                >
                  <FontAwesomeIcon icon={faPlus} /> Dodaj zmianę
                </button>
              )}
            </Step>
          </div>

          <div className={styles.footer}>
            <span className={styles.hint}>
              {hasErrors && showErrors
                ? deferValidation
                  ? "Metryka niekompletna — można zapisać i uzupełnić później."
                  : "Uzupełnij brakujące dane."
                : ""}
            </span>
            <div className={styles.footerRight}>
              <Button text="Anuluj" color="error" onClick={onClose} />
              {!readOnly && (
                <Button
                  text={saveLabel}
                  onClick={handleSave}
                  loading={saving}
                />
              )}
            </div>
          </div>
        </div>
      </ModalFieldScope>
    </div>,
    document.body
  );
};

export default ContractCruMetricModal;
